import { ButtonActionType, ButtonWidgetData, LinkButtonActionPayload } from './template-builder-button-widget.model';
import { TemplateBuilderContainerProperties } from './template-builder-container-properties';
import { ImageWidgetData } from './template-builder-image-widget.model';
import { MapWidgetData } from './template-builder-map-widget.model';
import { ResponsiveOptions } from './template-builder-responsive-options';
import { TextWidgetData } from './template-builder-text-widget.model';
import { VideoWidgetData } from './template-builder-video-widget.model';
import { generateShortUuid } from 'ag-common-svc/shared/utils/generate-item-uuid';
import { HeadingWidgetData } from '../models/template-builder-headings-widget.model';
import { SeparatorWidgetData } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-separator-widget.model';
import { SpacerWidgetData } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-spacer-widget.model';
import { ContactFormWidgetData } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-contact-us-widget.model';
import { FormWidgetData } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-form-widget.model';
import { ExcursionsTableWidgetData } from 'ag-common-svc/lib/components/administration/template-builder/template-editor-page/models/template-builder-excersions-table-widget.model';
import { EmailDynamicWidgetData } from './template-builder-email-dynamic-widget.model';
import { TemplateBuilderConditions } from './template-builder-conditions.model';
import { SocialMediaWidgetData } from './template-builder-social-media-widget.model';

import { ListWidgetData } from './template-builder-list-widget.model';

export enum TemplateBuilderWidgetTypes {
  list = 'list',
  text = 'text',
  button = 'button',
  image = 'image',
  video = 'video',
  map = 'map',
  separator = 'separator',
  spacer = 'spacer',
  contactUsForm = 'contactUsForm',
  form = 'form',
  heading = 'heading',
  excursionsTable = 'excursionsTable',
  emailRegistrationDetailsTable = 'emailRegistrationDetailsTable',
  emailExcursionsTable = 'emailExcursionsTable',
  emailTransactionsTable = 'emailTransactionsTable',
  emailActivitiesForDay = 'emailActivitiesForDay',
  emailFormResultsTable = 'emailFormResultsTable',
  emailConferenceImage = 'emailConferenceImage',
  socialMedia = 'socialMedia',
}

type TemplateBuilderWidgetDataTypeMap = {
  [TemplateBuilderWidgetTypes.list]: ListWidgetData;
  [TemplateBuilderWidgetTypes.text]: TextWidgetData;
  [TemplateBuilderWidgetTypes.heading]: TextWidgetData;
  [TemplateBuilderWidgetTypes.image]: ImageWidgetData;
  [TemplateBuilderWidgetTypes.button]: ButtonWidgetData;
  [TemplateBuilderWidgetTypes.video]: VideoWidgetData;
  [TemplateBuilderWidgetTypes.map]: MapWidgetData;
  [TemplateBuilderWidgetTypes.separator]: SeparatorWidgetData;
  [TemplateBuilderWidgetTypes.spacer]: SpacerWidgetData;
  [TemplateBuilderWidgetTypes.contactUsForm]: ContactFormWidgetData;
  [TemplateBuilderWidgetTypes.form]: FormWidgetData;
  [TemplateBuilderWidgetTypes.excursionsTable]: ExcursionsTableWidgetData;
  [TemplateBuilderWidgetTypes.emailRegistrationDetailsTable]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.emailExcursionsTable]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.emailTransactionsTable]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.emailActivitiesForDay]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.emailFormResultsTable]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.emailConferenceImage]: EmailDynamicWidgetData;
  [TemplateBuilderWidgetTypes.socialMedia]: SocialMediaWidgetData;
};

type TemplateBuilderWidgetDataType<T extends TemplateBuilderWidgetTypes> = TemplateBuilderWidgetDataTypeMap[T];

export const TemplateBuilderWidgetTypesMap = new Map([
  [TemplateBuilderWidgetTypes.text, 'Text Settings'],
  [TemplateBuilderWidgetTypes.list, 'List Settings'],
]);

export enum TemplateBuilderWidgetKeys {
  id = 'id',
  type = 'type',
  desktopData = 'desktopData',
  mobileData = 'mobileData',
  desktopContainerProperties = 'desktopContainerProperties',
  mobileContainerProperties = 'mobileContainerProperties',
  responsiveOptions = 'responsiveOptions',
}

export class TemplateBuilderWidget<T extends TemplateBuilderWidgetTypes = TemplateBuilderWidgetTypes> {
  [TemplateBuilderWidgetKeys.id]: string;
  [TemplateBuilderWidgetKeys.type]: T;
  [TemplateBuilderWidgetKeys.desktopData]?: TemplateBuilderWidgetDataType<T>;
  [TemplateBuilderWidgetKeys.mobileData]?: Partial<TemplateBuilderWidgetDataType<T>> = {};
  [TemplateBuilderWidgetKeys.desktopContainerProperties]: TemplateBuilderContainerProperties =
    new TemplateBuilderContainerProperties();
  [TemplateBuilderWidgetKeys.mobileContainerProperties]: Partial<TemplateBuilderContainerProperties> = {};
  [TemplateBuilderWidgetKeys.responsiveOptions]: ResponsiveOptions = new ResponsiveOptions();
  conditions?: TemplateBuilderConditions;

  constructor(type: T, data?: TemplateBuilderWidgetDataType<T>) {
    this[TemplateBuilderWidgetKeys.id] = generateShortUuid();
    this[TemplateBuilderWidgetKeys.type] = type;
    if (data) {
      this[TemplateBuilderWidgetKeys.desktopData] = data;
    }
  }
}

export const DEFAULT_WIDGETS: TemplateBuilderWidget[] = [
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.list, new ListWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.text, new TextWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.heading, new HeadingWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.image, new ImageWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.video, new VideoWidgetData()),
  new TemplateBuilderWidget(
    TemplateBuilderWidgetTypes.button,
    new ButtonWidgetData(ButtonActionType.link, new LinkButtonActionPayload()),
  ),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.map, new MapWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.separator, new SeparatorWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.spacer, new SpacerWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.form, new FormWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.excursionsTable, new ExcursionsTableWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailRegistrationDetailsTable, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailExcursionsTable, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailTransactionsTable, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailActivitiesForDay, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailFormResultsTable, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.emailConferenceImage, new EmailDynamicWidgetData()),
  new TemplateBuilderWidget(TemplateBuilderWidgetTypes.socialMedia, new SocialMediaWidgetData()),
];
